import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import MeditationListSummaryFilterSection from './components/MysteryBoxSummaryFilterSection';
import SetMeditationOrderDialog from './components/SetMysteryBoxOrderDialog';
import { PUBLISHED_LISTB } from '@/common/constants/common';
import PublishedChip from '@/common/components/PublishedChip';
import { BlindBoxDTO, BoxFilter } from '@/api/DTO/exchange/box.interface';
import { useGetBoxListQuery } from '@/api/exchange/boxApiSlice';

export interface DataGrid {
	updatedAt?: string;
	createdAt?: string;
	publishedStartAt?: string;
	publishedEndAt?: string;
	id?: string;
	published?: number | string | boolean;
}

export default function MysteryBoxSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();

	const defaultPropertyFilter: BoxFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		publishedList: [],
		boxTypeList: [],
		gameTypeList: [],
	};
	const defaultValues: DefaultValues<BoxFilter> = defaultPropertyFilter;
	const methods = useForm<BoxFilter>({
		defaultValues,
	});
	const transformedSearchParams: BoxFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'publishedList',
			'boxTypeList',
			'gameTypeList',
		]),
	};
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetBoxListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback((response?: BlindBoxDTO[]) => {
		if (response == null) return;
		const newResponse: DataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				createdAt: !item.createdAt
					? '-'
					: dayjs(item.createdAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
				publishedStartAt: !item.publishedStartAt
					? '-'
					: dayjs(item.publishedStartAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
				publishedEndAt: !item.publishedEndAt
					? '-'
					: dayjs(item.publishedEndAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as BoxFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const columns: GridColDef[] = [
		{
			field: 'boxTypeName',
			headerName: t('box.boxType'),
			flex: 10,
		},
		{
			field: 'gameTypeName',
			headerName: t('box.seriesType'),
			flex: 10,
			renderCell: (params: any) => params.row.gameTypeName ?? '-',
		},
		{
			field: 'titleZh',
			headerName: t('box.boxName'),
			flex: 20,
		},
		{
			field: 'taskCount',
			headerName: t('box.taskCount'),
			flex: 10,
		},
		{
			field: 'costPoints',
			headerName: t('box.costPoints'),
			flex: 10,
		},
		{
			field: 'published',
			headerName: t('box.boxStatus'),
			flex: 10,
			renderCell: (params: any) => (
				<PublishedChip
					published={params.row.published}
					publishList={PUBLISHED_LISTB}
				/>
			),
		},
		{
			field: 'publishedStartAt',
			headerName: t('achievementList.pushedTime'),
			flex: 10,
		},
		{
			field: 'publishedEndAt',
			headerName: t('achievementList.pulledTime'),
			flex: 10,
		},
		{
			field: 'updatedAt',
			headerName: t('achievementList.updateTime'),
			flex: 10,
		},

		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/exchange-manage/mystery-box/info/${params.id}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>{t('drawerListItems.mysteryBox')}</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<SetMeditationOrderDialog />
					<Button
						variant='contained'
						style={{ alignItems: 'center' }}
						endIcon={<NoteAltOutlinedIcon />}
						onClick={() => navigate(`/exchange-manage/mystery-box/add`)}
					>
						{t('box.createBox')}
					</Button>
				</Stack>
			</Box>

			<Card sx={{ marginTop: 1 }}>
				<CardContent>
					<FormProvider {...methods}>
						<MeditationListSummaryFilterSection></MeditationListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
